import React, { useEffect, useRef, useState, useCallback } from "react";

/* ══════════════════════════════════════════════════════════════════════
   ASSAYO — THE MATURITY SPECTRUM
   A horizontal calibration stack: 8 parallel measurement axes showing
   current-state vs target-state maturity. A precision scanner sweeps
   horizontally, pausing at each dimension to take evidence-backed readings.
   Pure SVG + CSS keyframes + React state (no Three.js / Framer Motion)
   MEASURE · COMPARE · TRANSFORM
   ══════════════════════════════════════════════════════════════════════ */

const W = 680, H = 560;
const BAR_X = 155, BAR_W = 360, BAR_H = 16;
const FIRST_Y = 68, ROW_H = 48;
const LABEL_X = 149;
const SCORE_X = 530;
const AGG_X = 610;

const DIMS = [
  { id: "strat",  label: "STRATEGIC ALIGNMENT",       sub: "Portfolio \u00b7 Value mgmt \u00b7 Governance", current: 62, target: 85, evidence: "Portfolio value mgmt 2/5 \u00b7 Sponsor engagement adequate",    gap_note: "Gap: 23 pts \u00b7 PMO strengthening recommended" },
  { id: "gov",    label: "PROJECT GOVERNANCE",         sub: "Stage-gate \u00b7 FEL \u00b7 Sponsor eng.",      current: 74, target: 90, evidence: "Stage-gate rigor 4/5 \u00b7 FEL gates well-defined",          gap_note: "Gap: 16 pts \u00b7 Assurance reviews strengthening needed" },
  { id: "fel",    label: "FRONT-END LOADING",          sub: "PDRI \u00b7 Scope def. \u00b7 Risk",             current: 58, target: 82, evidence: "PDRI score 620 \u00b7 FEL-3 deliverables 71% complete",       gap_note: "Gap: 24 pts \u00b7 Scope definition maturity critical" },
  { id: "ctrl",   label: "PROJECT CONTROLS",           sub: "Schedule \u00b7 Cost \u00b7 EVM \u00b7 Reporting", current: 81, target: 92, evidence: "EVM mature \u00b7 CPI 0.94 \u00b7 Schedule rigor high",           gap_note: "Gap: 11 pts \u00b7 Refinement opportunities only" },
  { id: "proc",   label: "PROCUREMENT & SUPPLY",    sub: "Category \u00b7 Sourcing \u00b7 Claims",         current: 67, target: 88, evidence: "Category strategy 3/5 \u00b7 Contract rigor adequate",        gap_note: "Gap: 21 pts \u00b7 Claims readiness needs strengthening" },
  { id: "eng",    label: "ENGINEERING DELIVERY",       sub: "P&ID \u00b7 3D \u00b7 MTO \u00b7 Interfaces",     current: 72, target: 85, evidence: "P&ID rigor 4/5 \u00b7 3D model governance strong",            gap_note: "Gap: 13 pts \u00b7 Interface management refinement" },
  { id: "const",  label: "CONSTRUCTION EXECUTION",     sub: "AWP \u00b7 Productivity \u00b7 HSE",              current: 54, target: 80, evidence: "AWP maturity 2/5 \u00b7 Field productivity below peer",       gap_note: "Gap: 26 pts \u00b7 AWP rollout critical transformation" },
  { id: "comm",   label: "COMMISSIONING & HANDOV.",   sub: "PreComm \u00b7 Systems \u00b7 Turnover",         current: 48, target: 78, evidence: "PreComm planning 2/5 \u00b7 Systems completion weak",         gap_note: "Gap: 30 pts \u00b7 Commissioning-first maturity needed" },
];

const TEMPLATES = [
  "CPMA \u00b7 CAPITAL PROJECTS MATURITY \u00b7 80 QUESTIONS",
  "PSCM \u00b7 PROCUREMENT MATURITY \u00b7 62 QUESTIONS",
  "DIGITAL \u00b7 TRANSFORMATION MATURITY \u00b7 54 QUESTIONS",
];

const ZONES = [
  { label: "INITIAL",    x: BAR_X + BAR_W * 0.125 },
  { label: "DEVELOPING", x: BAR_X + BAR_W * 0.375 },
  { label: "DEFINED",    x: BAR_X + BAR_W * 0.625 },
  { label: "OPTIMIZED",  x: BAR_X + BAR_W * 0.875 },
];

const AGG_CURRENT = Math.round(DIMS.reduce((s, d) => s + d.current, 0) / DIMS.length);
const AGG_TARGET = Math.round(DIMS.reduce((s, d) => s + d.target, 0) / DIMS.length);

/* ── Scanner keyframes: visits each dim's current-score x position ── */
function buildScannerPath() {
  const stops = DIMS.map((d, i) => ({
    dimIdx: i,
    xFrac: d.current / 100,
    x: BAR_X + BAR_W * (d.current / 100),
    holdMs: (d.target - d.current) > 20 ? 2200 : 1600,
  }));
  // Visit order: 0,1,2 (back to 2 re-check), 3,4,5,6,7
  return [stops[0], stops[1], stops[2], stops[3], stops[4], stops[5], stops[6], stops[7]];
}
const SCAN_PATH = buildScannerPath();
const CYCLE_MS = 20000;

export default function PariksaMaturitySpectrum() {
  const [reduceMotion, setReduceMotion] = useState(false);
  const [scannerX, setScannerX] = useState(BAR_X);
  const [activeDim, setActiveDim] = useState(-1);
  const [cardVisible, setCardVisible] = useState(false);
  const [templateIdx, setTemplateIdx] = useState(0);
  const [aggDisplay, setAggDisplay] = useState(AGG_CURRENT);
  const rafRef = useRef(null);
  const startRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const h = (e) => setReduceMotion(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);

  const animate = useCallback(() => {
    if (!startRef.current) startRef.current = performance.now();
    const elapsed = (performance.now() - startRef.current) % CYCLE_MS;
    const t = elapsed / CYCLE_MS; // 0→1

    // Compute scanner position and active dimension based on time
    const totalStops = SCAN_PATH.length;
    const segTime = 1 / totalStops;
    const segIdx = Math.min(Math.floor(t / segTime), totalStops - 1);
    const segProgress = (t - segIdx * segTime) / segTime;

    const currentStop = SCAN_PATH[segIdx];
    const nextStop = SCAN_PATH[Math.min(segIdx + 1, totalStops - 1)];

    // Eased movement between stops
    const eased = segProgress < 0.3 ? 0 : segProgress > 0.7 ? 1 : (segProgress - 0.3) / 0.4;
    const smoothEased = eased * eased * (3 - 2 * eased); // smoothstep
    const x = currentStop.x + (nextStop.x - currentStop.x) * smoothEased;
    setScannerX(x);

    // During the hold phase (middle 40% of each segment), show card
    const inHold = segProgress > 0.15 && segProgress < 0.65;
    setActiveDim(inHold ? currentStop.dimIdx : -1);
    setCardVisible(inHold);

    // Aggregate jitter
    if (Math.random() < 0.008) {
      setAggDisplay(AGG_CURRENT + (Math.random() > 0.5 ? 1 : -1));
      setTimeout(() => setAggDisplay(AGG_CURRENT), 400);
    }

    // Template rotation at cycle boundary
    if (t < 0.01) {
      setTemplateIdx(prev => (prev + 1) % TEMPLATES.length);
    }

    rafRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    if (reduceMotion) return;
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [reduceMotion, animate]);

  // Reduced motion: static frame
  if (reduceMotion) {
    return (
      <div className="pk-spec-wrap" data-testid="pk-spectrum">
        <svg viewBox={`0 0 ${W} ${H}`} className="pk-spec-svg" aria-label="Maturity spectrum: 8 capability dimensions measured with current vs target scores and gap analysis">
          <TopOverlay template={TEMPLATES[0]} />
          <ZoneBands />
          {DIMS.map((d, i) => <CalibrationBar key={d.id} dim={d} idx={i} />)}
          <AggregateScore value={AGG_CURRENT} target={AGG_TARGET} />
          <ScannerLine x={BAR_X + BAR_W * 0.58} />
          <ReadingCard dim={DIMS[2]} idx={2} scannerX={BAR_X + BAR_W * 0.58} visible />
          <BottomLabels />
        </svg>
      </div>
    );
  }

  return (
    <div className="pk-spec-wrap" data-testid="pk-spectrum">
      <svg viewBox={`0 0 ${W} ${H}`} className="pk-spec-svg" aria-label="Animated maturity spectrum measuring 8 capability dimensions with a precision scanner">
        <defs>
          <pattern id="pk-hatch" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="6" className="pk-hatch-line" />
          </pattern>
          <pattern id="pk-hatch-strong" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="6" className="pk-hatch-line-strong" />
          </pattern>
          <linearGradient id="pk-fill-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" className="pk-fill-start" />
            <stop offset="100%" className="pk-fill-end" />
          </linearGradient>
        </defs>
        <TopOverlay template={TEMPLATES[templateIdx]} />
        <ZoneBands />
        {DIMS.map((d, i) => <CalibrationBar key={d.id} dim={d} idx={i} />)}
        <AggregateScore value={aggDisplay} target={AGG_TARGET} />
        <ScannerLine x={scannerX} />
        {activeDim >= 0 && cardVisible && (
          <ReadingCard dim={DIMS[activeDim]} idx={activeDim} scannerX={scannerX} visible />
        )}
        <BottomLabels />
      </svg>
    </div>
  );
}

/* ── Top Overlay ── */
function TopOverlay({ template }) {
  return (
    <g className="pk-top-overlay">
      <text x={10} y={18} className="pk-ov-text">{template}</text>
      <text x={W / 2} y={18} textAnchor="middle" className="pk-ov-text pk-ov-live">LIVE {"\u00b7"} MEASURING</text>
      <text x={W - 10} y={18} textAnchor="end" className="pk-ov-text pk-ov-ai">{"\u26a1"} 17 TEMPLATES {"\u00b7"} CLAUDE OPUS 4.6</text>
      <line x1={0} y1={28} x2={W} y2={28} className="pk-ov-line" />
    </g>
  );
}

/* ── Zone Bands ── */
function ZoneBands() {
  const zoneW = BAR_W / 4;
  return (
    <g className="pk-zones" aria-hidden="true">
      {[0, 1, 2, 3].map(i => (
        <rect key={i} x={BAR_X + i * zoneW} y={FIRST_Y - 18} width={zoneW} height={ROW_H * 8 + 10}
          className={`pk-zone-bg pk-zone-bg-${i}`} />
      ))}
      {[0.25, 0.5, 0.75].map((f, i) => (
        <line key={i} x1={BAR_X + BAR_W * f} y1={FIRST_Y - 18} x2={BAR_X + BAR_W * f} y2={FIRST_Y + ROW_H * 8 - 8}
          className="pk-zone-div" />
      ))}
      {ZONES.map((z, i) => (
        <text key={i} x={z.x} y={FIRST_Y - 6} textAnchor="middle" className="pk-zone-label">{z.label}</text>
      ))}
    </g>
  );
}

/* ── Single Calibration Bar ── */
function CalibrationBar({ dim, idx }) {
  const y = FIRST_Y + idx * ROW_H;
  const currentW = BAR_W * (dim.current / 100);
  const targetW = BAR_W * (dim.target / 100);
  const gapW = targetW - currentW;
  const gap = dim.target - dim.current;
  const isSignificant = gap > 25;
  const isAttention = gap > 20;

  return (
    <g className="pk-bar-group" data-testid={`pk-bar-${dim.id}`}>
      {/* Dimension label */}
      <text x={LABEL_X} y={y + BAR_H / 2 - 2} textAnchor="end" className="pk-dim-label">{dim.label}</text>
      <text x={LABEL_X} y={y + BAR_H / 2 + 10} textAnchor="end" className="pk-dim-sub">{dim.sub}</text>

      {/* Bar track */}
      <rect x={BAR_X} y={y} width={BAR_W} height={BAR_H} rx={2} className="pk-bar-track" />

      {/* Tick marks */}
      {[0, 10, 20, 25, 30, 40, 50, 60, 70, 75, 80, 90, 100].map(v => {
        const tx = BAR_X + BAR_W * (v / 100);
        const isMajor = v % 25 === 0;
        return <line key={v} x1={tx} y1={y + BAR_H} x2={tx} y2={y + BAR_H + (isMajor ? 4 : 2)}
          className={isMajor ? "pk-tick-major" : "pk-tick"} />;
      })}

      {/* Target outline (dashed) */}
      <rect x={BAR_X} y={y} width={targetW} height={BAR_H} rx={2}
        fill="none" className="pk-target-outline"
        style={{ animationDelay: `${idx * 0.7}s` }} />
      {/* Target diamond marker */}
      <polygon points={`${BAR_X + targetW},${y - 3} ${BAR_X + targetW + 4},${y + BAR_H / 2} ${BAR_X + targetW},${y + BAR_H + 3} ${BAR_X + targetW - 4},${y + BAR_H / 2}`}
        className="pk-target-diamond" />

      {/* Gap region (hatch) */}
      {gap > 0 && (
        <rect x={BAR_X + currentW} y={y} width={gapW} height={BAR_H} rx={0}
          fill={isAttention ? "url(#pk-hatch-strong)" : "url(#pk-hatch)"}
          className="pk-gap-region"
          style={{ animationDelay: `${idx * 0.5}s` }} />
      )}

      {/* Current fill */}
      <rect x={BAR_X} y={y} width={currentW} height={BAR_H} rx={2}
        className="pk-current-fill"
        style={{ animationDelay: `${idx * 0.6}s` }} />
      {/* Current fill top rim */}
      <line x1={BAR_X} y1={y} x2={BAR_X + currentW} y2={y} className="pk-fill-rim" />
      {/* Current score dot */}
      <circle cx={BAR_X + currentW} cy={y + BAR_H / 2} r={3}
        className="pk-score-dot"
        style={{ animationDelay: `${idx * 0.4}s` }} />

      {/* Significant gap amber marker */}
      {isSignificant && (
        <circle cx={BAR_X + BAR_W + 8} cy={y + BAR_H / 2} r={3} className="pk-gap-amber" />
      )}

      {/* Score numerics */}
      <text x={SCORE_X} y={y + BAR_H / 2 + 1} textAnchor="start"
        className={isSignificant ? "pk-score-num pk-score-amber" : "pk-score-num"}>
        {dim.current}
      </text>
      <text x={SCORE_X} y={y + BAR_H / 2 + 13} textAnchor="start" className="pk-score-target">
        {"\u2192"} {dim.target}
      </text>
    </g>
  );
}

/* ── Scanner Line ── */
function ScannerLine({ x }) {
  const pct = Math.round(((x - BAR_X) / BAR_W) * 100);
  return (
    <g className="pk-scanner" aria-hidden="true">
      <line x1={x} y1={FIRST_Y - 18} x2={x} y2={FIRST_Y + ROW_H * 8 - 8} className="pk-scan-line" />
      <rect x={x - 20} y={FIRST_Y - 32} width={40} height={14} rx={2} className="pk-scan-plate" />
      <text x={x} y={FIRST_Y - 22} textAnchor="middle" className="pk-scan-pct">{Math.max(0, Math.min(100, pct))}%</text>
    </g>
  );
}

/* ── Reading Card ── */
function ReadingCard({ dim, idx, scannerX, visible }) {
  if (!visible || !dim) return null;
  const y = FIRST_Y + idx * ROW_H;
  const gap = dim.target - dim.current;
  const cardW = 200, cardH = 72;
  // Position card to the right of scanner if scanner is in left half, else left
  const onRight = scannerX < BAR_X + BAR_W / 2;
  const cx = onRight ? scannerX + 24 : scannerX - cardW - 24;
  const cy = Math.max(40, Math.min(H - cardH - 40, y - 10));

  return (
    <g className="pk-reading-card pk-card-enter" aria-hidden="true">
      {/* Connection line */}
      <line x1={scannerX} y1={y + BAR_H / 2} x2={onRight ? cx : cx + cardW} y2={cy + cardH / 2}
        className="pk-card-conn" />
      <rect x={cx} y={cy} width={cardW} height={cardH} rx={6} className="pk-card-bg" />
      {/* Orange spark */}
      <text x={cx + 10} y={cy + 14} className="pk-card-spark">{"\u26a1"}</text>
      {/* Row 1: score */}
      <text x={cx + 24} y={cy + 14} className="pk-card-title">{dim.current} / 100</text>
      {/* Row 2: dimension name */}
      <text x={cx + 10} y={cy + 30} className="pk-card-evidence">{dim.label}</text>
      {/* Row 3: evidence */}
      <text x={cx + 10} y={cy + 46} className="pk-card-evidence">{dim.evidence.slice(0, 42)}</text>
      {/* Row 4: gap */}
      <text x={cx + 10} y={cy + 62} className={gap > 20 ? "pk-card-gap pk-card-gap-amber" : "pk-card-gap"}>
        {dim.gap_note.slice(0, 44)}
      </text>
    </g>
  );
}

/* ── Aggregate Score ── */
function AggregateScore({ value, target }) {
  const gap = target - value;
  return (
    <g className="pk-aggregate" data-testid="pk-aggregate">
      <text x={AGG_X} y={FIRST_Y + 40} textAnchor="middle" className="pk-agg-label">OVERALL</text>
      <text x={AGG_X} y={FIRST_Y + 44} textAnchor="middle" className="pk-agg-label">MATURITY</text>
      <text x={AGG_X} y={FIRST_Y + 110} textAnchor="middle" className="pk-agg-num">{value}</text>
      <text x={AGG_X} y={FIRST_Y + 130} textAnchor="middle" className="pk-agg-unit">/ 100</text>
      <text x={AGG_X} y={FIRST_Y + 158} textAnchor="middle" className="pk-agg-target">{"\u2192"} {target} TARGET</text>
      <text x={AGG_X} y={FIRST_Y + 178} textAnchor="middle" className="pk-agg-gap">GAP {"\u00b7"} {gap} PTS</text>
      {/* Pulse ring */}
      <circle cx={AGG_X} cy={FIRST_Y + 100} r={32} className="pk-agg-ring" />
    </g>
  );
}

/* ── Bottom Labels ── */
function BottomLabels() {
  return (
    <g className="pk-bottom-labels">
      <text x={BAR_X + 30} y={H - 12} className="pk-verb pk-verb-1">MEASURE</text>
      <text x={BAR_X + BAR_W / 2} y={H - 12} textAnchor="middle" className="pk-verb pk-verb-2">COMPARE</text>
      <text x={BAR_X + BAR_W - 30} y={H - 12} textAnchor="end" className="pk-verb pk-verb-3">TRANSFORM</text>
    </g>
  );
}
